import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "../../components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import { createSellerManualPayout } from "../../api/admin.js";
import { addToast } from "../../store/app/action.js";

const MAX_PAYOUT_DOLLARS = 500;

function SellerManualPayoutModal({ store, setModalOpen }) {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!store) return;
    setAmount("");
    setDescription("");
    setSubmitting(false);
  }, [store]);

  if (!store) return null;

  const parsedAmount = parseFloat(String(amount).replace(/^\$/, "").trim());
  const isValidAmount =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    parsedAmount <= MAX_PAYOUT_DOLLARS;
  const isValidDescription = description.trim().length > 0;
  const amountOverLimit =
    Number.isFinite(parsedAmount) && parsedAmount > MAX_PAYOUT_DOLLARS;
  const canSubmit = isValidAmount && isValidDescription && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await createSellerManualPayout(store.id, {
        amount: parsedAmount,
        description: description.trim(),
      });
      dispatch(
        addToast({
          type: "success",
          message: `Paid $${parsedAmount.toFixed(2)} to ${store.name}`,
          timer: 8,
        })
      );
      if (result.dashboard_url) {
        window.open(result.dashboard_url, "_blank", "noopener,noreferrer");
      }
      setModalOpen(null);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Payout failed";
      dispatch(
        addToast({
          type: "error",
          message,
          timer: 8,
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBasic
      width="md"
      modalOpen={Boolean(store)}
      setModalOpen={setModalOpen}
      title="Manual seller payout"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Transfer funds to{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-100">
            {store.name}
          </span>{" "}
          ({store.store_id}). This runs immediately and cannot be undone.
        </p>
        <div className="text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
          Stripe: {store.stripe_account_id}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Amount (USD) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            className="form-input w-full"
            placeholder="30.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={submitting}
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Maximum ${MAX_PAYOUT_DOLLARS.toFixed(2)} per transfer.
          </p>
          {amountOverLimit && (
            <p className="text-xs text-red-500 mt-1">
              Amount exceeds the ${MAX_PAYOUT_DOLLARS} limit.
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Bank statement description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="form-input w-full"
            placeholder={`SCY REIMB ${store.store_id}-XXXXX`}
            maxLength={22}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Shown on the seller&apos;s bank statement (max 22 characters).
          </p>
        </div>
        <div className="flex gap-2 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
          <SecondaryButton
            type="button"
            onClick={() => setModalOpen(null)}
            disabled={submitting}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" loading={submitting} disabled={!canSubmit}>
            Confirm payout
          </PrimaryButton>
        </div>
      </form>
    </ModalBasic>
  );
}

export default SellerManualPayoutModal;
